<template>
  <div class="user-roles">
    <r-main-panel :title="`${$t('user-roles:title')}`" icon="user-group">
      <users-list :is-loading="isLoading" :users="allUsers" />
    </r-main-panel>
    <div class="user-roles__content">
      <user-roles-bg v-if="!activeUser" />
      <user-info v-else-if="activeUser" :users="allUsers" />
    </div>
  </div>
</template>

<script>
import usersList from "./components/user-roles-user-list";
import userRolesBg from "./components/user-roles-bg";
import userInfo from "./components/user-roles-user-info";

import { usersRequestConfig } from "./configs/index.js";

import { errorParser } from "@/utils";

export default {
  components: {
    usersList,
    userRolesBg,
    userInfo
  },
  data() {
    return {
      isLoading: false,
      allUsers: []
    };
  },
  watch: {
    usersUpdate(val) {
      if (!val) return;
      this.loadUsers(true);
      this.$store.commit("SET_USER_ROLES_USERS_UPDATE", false);
    }
  },
  computed: {
    redirectedId() {
      return this.$route.query.id || null;
    },
    activeUser() {
      return this.$store.state.userRoles.activeUser || null;
    },
    usersUpdate() {
      return this.$store.state.userRoles.usersUpdate;
    }
  },
  methods: {
    async loadUsers(newUser, initial) {
      this.isLoading = true;
      try {
        await this.$store
          .dispatch("REQUEST", {
            url: `objectInfo/auth.users?config=${JSON.stringify(
              usersRequestConfig
            )}`
          })
          .then(({ data }) => {
            this.allUsers = Object.keys(data).map(item => data[item]);
          });
        this.isLoading = false;
      } catch (error) {
        const title = this.$t("user-roles:notify:error-title");
        const message = this.$t("user-roles:notify:error-text");

        errorParser.call(this, error, message, title);
        this.isLoading = false;
      }
      if (newUser) this.$store.commit("SET_USER_ROLES_NEW_USER_READY", true);
      if (initial && this.redirectedId) {
        const user = this.allUsers.find(e => e.id === this.redirectedId);
        if (!user) return;
        this.setActiveUser(user);
      } else {
        if (this.activeUser) {
          const user = this.allUsers.find(e => e.name === this.activeUser.name);
          if (!user) return;
          this.setActiveUser(user);
        }
      }
    },
    loadAllPerms() {
      let allPerms;

      return this.$store
        .dispatch("REQUEST", { url: "permission_object_tree?" })
        .then(({ data }) => {
          allPerms = data;
          this.$store.commit("SET_USER_ROLES_ALL_PERMS", allPerms);
        });
    },
    async setActiveUser(user) {
      this.$store.commit("SET_USER_ROLES_LOADING", true);
      const { id } = user;
      const modules = await this.$store.dispatch("POST_REQUEST", {
        url: `user/modules?user_id=${id}`
      });
      const datasources = await this.$store.dispatch("POST_REQUEST", {
        url: `user/datasources?user_id=${id}`
      });

      const userModules = modules.data;
      const userDS = datasources.data.map(item => ({
        ...item,
        _action: undefined,
        create: item.cruds[0],
        read: item.cruds[1],
        update: item.cruds[2],
        delete: item.cruds[3],
        share: item.cruds[4],
        export: item.cruds[5] || false
      }));
      userDS.forEach(ds => {
        if (ds.children) {
          ds.children.forEach(child => {
            userDS.push({
              ...child,
              _action: undefined,
              create: child.cruds[0],
              read: child.cruds[1],
              update: child.cruds[2],
              delete: child.cruds[3],
              share: child.cruds[4],
              export: child.cruds[5] || false
            });
          });
        }
      });

      this.$store.commit("SET_ACTIVE_USER_ROLES_USER", {
        datasources: datasources.data,
        user,
        modules: userModules,
        ds: userDS
      });
      this.$store.commit("SET_INITIAL_USER_ROLES_USER", {
        datasources: datasources.data,
        user,
        modules: userModules,
        ds: userDS
      });
      this.userLoading = false;
      this.$store.commit("SET_USER_ROLES_LOADING", false);
    }
  },
  created() {
    this.loadUsers(false, true);
    this.loadAllPerms();
  }
};
</script>

<style lang="scss">
.user-roles {
  display: flex;
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
  background-color: var(--table_cell_bg) !important;
  color: var(--text_secondary) !important;
  &__content {
    width: 100%;
    height: 100%;
    position: relative;
    overflow: hidden;
  }
}
</style>

<i18n>
{
  "ru": {
    "user-roles:title": "Роли и разрешения",

    "user-roles:notify:error-title": "Ошибка при загрузке данных",
    "user-roles:notify:error-text": "Загрузка данных модуля не выполнена. Перезапустите, пожалуйста, модуль."
  },
  "en": {
    "user-roles:title": "User & Roles",

    "user-roles:notify:error-title": "Loading data error",
    "user-roles:notify:error-text": "Module data upload failed. Please restart the module."
  }
}
</i18n>
